import type { ShallowReactive } from 'vue';
import { isPromise } from '@vesium/shared';
import { tryOnScopeDispose } from '@vueuse/core';
import { shallowReactive, shallowReadonly } from 'vue';

export type EffcetRemovePredicate<T> = (instance: T) => boolean;

export interface UseCollectionScopeOptions<
  T,
  AddArgs extends any[] = any[],
  RemoveArgs extends any[] = [],
  RemoveReturn = any,
> {
  /**
   * add SideEffect function.  e.g. `entities.add`
   */
  addEffect: (instance: T | Promise<T>, ...args: AddArgs) => T | Promise<T>;

  /**
   * Clean SideEffect function.  eg.`entities.remove`
   */
  removeEffect: (instance: T, ...args: RemoveArgs) => RemoveReturn;

  /**
   * The parameters to pass for `removeScope` triggered when the component is unmounted
   */
  removeScopeArgs?: RemoveArgs;
}

export interface UseCollectionScopeReturn<
  T,
  AddArgs extends any[],
  RemoveArgs extends any[],
  RemoveReturn = any,
> {
  /**
   * A `Set` for storing SideEffect instance,
   * which is encapsulated using `ShallowReactive` to provide Vue's reactive functionality
   */
  scope: Readonly<ShallowReactive<Set<T>>>;

  /**
   * Add SideEffect instance
   */
  add: <R extends T | Promise<T>>(instance: R, ...args: AddArgs) => R extends Promise<infer U> ? Promise<U> : T;

  /**
   * Remove specified SideEffect instance
   */
  remove: (instance: T, ...args: RemoveArgs) => RemoveReturn;

  /**
   * Remove all SideEffect instance that meets the specified criteria
   */
  removeWhere: (predicate: EffcetRemovePredicate<T>, ...args: RemoveArgs) => void;

  /**
   * Remove all SideEffect instance within current scope
   */
  removeScope: (...args: RemoveArgs) => void;
}

/**
 * Scope the SideEffects of Cesium-related `Collection` and automatically remove them when unmounted.
 * - note: This is a basic function that is intended to be called by other lower-level function
 * @returns Contains side effect addition and removal functions
 */
export function useCollectionScope<
  T,
  AddArgs extends any[] = any[],
  RemoveArgs extends any[] = [],
  RemoveReturn = any,
>(
  options: UseCollectionScopeOptions<T, AddArgs, RemoveArgs, RemoveReturn>,
): UseCollectionScopeReturn<T, AddArgs, RemoveArgs, RemoveReturn> {
  const { addEffect, removeEffect, removeScopeArgs } = options;
  const scope = shallowReactive(new Set<T>());
  const add = (instance: T | Promise<T>, ...args: AddArgs) => {
    const result = addEffect(instance, ...args);
    // 可能为promise 如dataSource
    if (isPromise(result)) {
      return new Promise<T>((resolve, reject) => {
        result.then((i: T) => {
          scope.add(i);
          resolve(i);
        }).catch(error => reject(error));
      });
    }
    else {
      scope.add(result as T);
      return result;
    }
  };

  const remove: typeof removeEffect = (instance, ...args) => {
    scope.delete(instance);
    return removeEffect(instance, ...args);
  };

  const removeWhere = (predicate: EffcetRemovePredicate<T>, ...args: RemoveArgs) => {
    for (const instance of Array.from(scope)) {
      if (predicate(instance)) {
        remove(instance, ...args);
      }
    }
  };

  const removeScope = (...args: RemoveArgs) => {
    for (const instance of Array.from(scope)) {
      remove(instance, ...args);
    }
  };

  tryOnScopeDispose(() => removeScope(...(removeScopeArgs ?? [] as RemoveArgs)));

  return {
    scope: shallowReadonly(scope),
    add: add as UseCollectionScopeReturn<T, AddArgs, RemoveArgs, RemoveReturn>['add'],
    remove,
    removeWhere,
    removeScope,
  };
}
